// Timeline projector API (v1) — uniform TimelineItem stream.
//
// Backed by /v1/cases/{case_id}/timeline/items, which reads schema-driven
// Neo4j data through the conventions module and returns a uniform shape
// regardless of whether the event is node- or edge-sourced.
import { apiClient } from './client'

export interface TimelineGeo {
  lat: number
  lon: number
}

export interface TimelineParticipant {
  id: string | null
  label: string | null
  type: string | null
  relation?: string
  /** Resolved geolocation for the participant: inline lat/lon on the
   *  participant's own properties, or the first single-hop OUT neighbor's
   *  inline lat/lon. ``null`` when the conventions module cannot resolve a
   *  location for this instance. Omitted entirely in compact mode — callers
   *  that need geo must fetch the full item details on demand. */
  geo?: TimelineGeo | null
}

export interface TimelineParticipants {
  primary: TimelineParticipant
  others: TimelineParticipant[]
}

export type TimelineSourceKind = 'node' | 'edge' | 'snippet'

interface TimelineItemBase {
  id: string
  source_kind: TimelineSourceKind
  source_type: string
  source_id: string
  source_label: string
  event_kind: string
  event_label: string
  participants: TimelineParticipants
  /** Per-property display bag. Omitted in compact mode — clients that need
   *  the full property set must fetch the underlying entity / relationship
   *  via its own detail endpoint when the user clicks the event. */
  display_props?: Record<string, unknown>
}

export interface TimelinePointItem extends TimelineItemBase {
  kind: 'point'
  at: string
}

export interface TimelineIntervalItem extends TimelineItemBase {
  kind: 'interval'
  start: string
  end: string | null
}

export type TimelineItem = TimelinePointItem | TimelineIntervalItem

export interface TimelineItemFilters {
  range_start?: string
  range_end?: string
  entity_types?: string[]
  event_kinds?: string[]
  participant_ids?: string[]
  geo_scope?: boolean
  /** Scope the projection to a specific set of node IDs (the active
   *  investigation's object set). When present, only items whose
   *  ``source_id`` or any participant (primary / others) is in this set are
   *  returned. Omit for the legacy whole-case projection. An explicit empty
   *  array returns an empty list (the backend short-circuits — no Cypher).
   *  Soft cap: a few hundred IDs per request to stay within common URL
   *  length limits; callers with larger sets should truncate client-side. */
  node_ids?: string[]
  /** When true, the backend omits ``display_props`` and ``participants.*.geo``
   *  from every item. The time-axis canvas only renders header + entity
   *  labels, so it should always pass ``compact: true`` — the full details
   *  are fetched per-item via the existing entity / relationship detail
   *  endpoints when the user clicks an event. */
  compact?: boolean
}

export interface TimelineHistogramBin {
  bin_start: string
  count: number
}

function buildQueryString(filters?: TimelineItemFilters): string {
  if (!filters) return ''
  const sp = new URLSearchParams()
  if (filters.range_start) sp.set('range_start', filters.range_start)
  if (filters.range_end) sp.set('range_end', filters.range_end)
  if (filters.geo_scope) sp.set('geo_scope', 'true')
  if (filters.compact) sp.set('compact', 'true')
  filters.entity_types?.forEach(v => sp.append('entity_types', v))
  filters.event_kinds?.forEach(v => sp.append('event_kinds', v))
  filters.participant_ids?.forEach(v => sp.append('participant_ids', v))
  // ``node_ids`` is meaningful even when the array is empty: an explicit
  // empty list is a "scope to no nodes" signal that the backend honours by
  // returning ``[]`` without hitting Neo4j. ``undefined`` means "no scope".
  if (filters.node_ids !== undefined) {
    if (filters.node_ids.length === 0) {
      // FastAPI parses repeated empty-value query params; we emit a single
      // empty value so the server sees ``node_ids: []`` rather than the
      // parameter being omitted altogether.
      sp.append('node_ids', '')
    } else {
      filters.node_ids.forEach(v => sp.append('node_ids', v))
    }
  }
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

export const timelineApi = {
  getItems: (caseId: string, filters?: TimelineItemFilters) =>
    apiClient.get<TimelineItem[]>(`/v1/cases/${caseId}/timeline/items${buildQueryString(filters)}`),

  getHistogram: (caseId: string, filters?: TimelineItemFilters) =>
    apiClient.get<TimelineHistogramBin[]>(
      `/v1/cases/${caseId}/timeline/histogram${buildQueryString(filters)}`
    ),
}
